import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ZoomInIcon, ZoomOutIcon, RotateCcwIcon, MoveIcon, Loader2 } from "lucide-react";

export interface FileViewerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  fileUrl: string;
  fileType: string;
  isLoading?: boolean;
}

export function FileViewer({
  isOpen,
  onOpenChange,
  fileName,
  fileUrl,
  fileType,
  isLoading = false
}: FileViewerProps) {
  const { t } = useTranslation();
  
  // Image viewer state
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);

  const isPdf = fileType === 'application/pdf';
  const isImage = fileType.startsWith('image/');

  // Reset zoom and position when modal closes
  useEffect(() => {
    if (!isOpen) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  // Image zoom functions
  const zoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 5));
  };

  const zoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Image drag handlers for panning
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (zoom > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({ 
        x: e.touches[0].clientX - position.x, 
        y: e.touches[0].clientY - position.y 
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isDragging && zoom > 1 && e.touches.length === 1) {
      e.preventDefault(); // Prevent page scrolling while panning
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[95vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{fileName}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden relative">
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : fileUrl ? (
            isPdf ? (
              <iframe 
                src={fileUrl} 
                className="w-full h-full border-0"
                title={fileName}
              />
            ) : isImage ? (
              <>
                {/* Image zoom controls */}
                <div className="absolute top-2 right-2 z-10 flex gap-2 bg-background/80 p-1 rounded-md shadow-sm">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={zoomIn} 
                    title={t('zoom_in')}
                    className="h-8 w-8"
                  >
                    <ZoomInIcon className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={zoomOut} 
                    title={t('zoom_out')}
                    className="h-8 w-8"
                  >
                    <ZoomOutIcon className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={resetZoom} 
                    title={t('reset_view')}
                    className="h-8 w-8"
                  >
                    <RotateCcwIcon className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Panning indicator */}
                {zoom > 1 && (
                  <div className="absolute bottom-2 right-2 z-10 bg-background/80 p-1 px-2 rounded-md shadow-sm text-xs flex items-center gap-1">
                    <MoveIcon className="h-3 w-3" />
                    {t('drag_to_move')}
                  </div>
                )}
                
                {/* Image container with pan functionality */}
                <div 
                  className="w-full h-full flex items-center justify-center overflow-hidden"
                  style={{ 
                    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <img 
                    draggable={false}
                    ref={imageRef}
                    src={fileUrl} 
                    alt={fileName} 
                    className="max-w-full max-h-full object-contain"
                    style={{ 
                      transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                      transition: isDragging ? 'none' : 'transform 0.2s ease',
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p>{t('unsupported_file_type')}</p>
              </div>
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p>{t('no_file_to_display')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}